import {
  useV2ListProjectApprovalsForAccountQuery,
  useWhoAmIQuery,
} from "@generated";
import { PendingApproval } from "../types";

const formatTimeWaiting = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";

  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days >= 1) return `${days} day${days === 1 ? "" : "s"}`;
  if (hours >= 1) return `${hours} hour${hours === 1 ? "" : "s"}`;
  if (minutes >= 1) return `${minutes} minute${minutes === 1 ? "" : "s"}`;
  return "less than a minute";
};

const usePendingApprovals = () => {
  const { data: me, isLoading: meLoading } = useWhoAmIQuery();
  const currentUserId = me?.data?.id?.toString();

  const {
    data,
    isLoading: approvalsLoading,
    error,
    refetch,
  } = useV2ListProjectApprovalsForAccountQuery(
    {
      filter: { "pending-user-approval": currentUserId ?? "" },
      include: ["project", "project.client" as any],
      page: { size: 100, number: 1 },
    },
    { skip: !currentUserId }
  );

  let pendingApprovals: PendingApproval[] = [];

  if (data?.data) {
    const included = (data.included ?? []) as Array<{
      id?: number | string;
      type?: string;
      attributes?: Record<string, unknown>;
      relationships?: Record<string, { data?: unknown }>;
    }>;

    const clientNamesById = new Map<string, string>();
    const projectsById = new Map<
      string,
      {
        projectName: string;
        contractRevenue: number;
        contractMargin: number;
        submittedAt: string | null;
        clientId: string | null;
      }
    >();

    for (const item of included) {
      if (item.type === "clients") {
        clientNamesById.set(
          String(item.id),
          (item.attributes?.name as string) ?? ""
        );
      } else if (item.type === "projects") {
        const clientRel = item.relationships?.client?.data as
          | { id?: number | string }
          | undefined;
        projectsById.set(String(item.id), {
          projectName: (item.attributes?.["project-name"] as string) ?? "",
          contractRevenue: Number(item.attributes?.["contract-revenue"] ?? 0),
          contractMargin: Number(item.attributes?.["contract-margin"] ?? 0),
          submittedAt: (item.attributes?.["submitted-at"] as string) ?? null,
          clientId: clientRel?.id ? String(clientRel.id) : null,
        });
      }
    }

    const seenProjectIds = new Set<string>();

    for (const approval of data.data) {
      const stepRel = (
        approval.relationships as Record<string, { data?: unknown }>
      )?.["approval-step"]?.data as { id?: number | string } | undefined;

      // Resolve project via the approval-step's project relationship from `included`
      // Fallback to a `project` relationship on the approval if the API surfaces it.
      const directProjectRel = (
        approval.relationships as Record<string, { data?: unknown }>
      )?.project?.data as { id?: number | string } | undefined;

      let projectId = directProjectRel?.id ? String(directProjectRel.id) : null;

      if (!projectId && stepRel?.id) {
        const stepIncluded = included.find(
          (i) =>
            i.type === "approval-steps" && String(i.id) === String(stepRel.id)
        );
        const stepProjectRel = stepIncluded?.relationships?.project?.data as
          | { id?: number | string }
          | undefined;
        if (stepProjectRel?.id) projectId = String(stepProjectRel.id);
      }

      if (!projectId || seenProjectIds.has(projectId)) continue;
      const project = projectsById.get(projectId);
      if (!project) continue;

      seenProjectIds.add(projectId);

      const clientName = project.clientId
        ? clientNamesById.get(project.clientId) ?? ""
        : "";

      pendingApprovals.push({
        id: projectId,
        projectName: project.projectName,
        clientName,
        contractRevenue: project.contractRevenue,
        contractMargin: project.contractMargin,
        submittedAt: project.submittedAt,
        timeWaiting: formatTimeWaiting(project.submittedAt),
      });
    }
  }

  return {
    data: pendingApprovals,
    isLoading: approvalsLoading || meLoading,
    error,
    refetch,
  };
};

export default usePendingApprovals;
